import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridHeaderConfiguration } from "@agtech/web/components/Grids/AgTechGridHeader";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import { SaleDiscount } from "@sales/data/sales/discounts/SaleDiscountData";
import { useAgTechQuery, useAgTechQueryResultProjection } from "@agtech/core/data/queries/AgTechDataQueries";
import { DiscountDataGetAllDiscountsQuery } from "@sales/data/sales/discounts/SaleDiscountDataQueries";
import { AgTechGridEntitySelectionIndex, mergeGridEntitySelections } from "@agtech/web/components/Grids/AgTechGridEntities";
import { AgTechClickableIcon } from "@agtech/web/components/Icons/AgTechIcons";
import SaleDiscountDialog from "../../../discounts/dialogs/SaleDiscountDialog";

export type InvoiceChangeDiscountsGridProps = {
    header?: AgTechGridHeaderConfiguration,
    discountSelections: AgTechGridEntitySelectionIndex<SaleDiscount>,
    onDiscountSelected: (d: SaleDiscount) => Promise<void>
    onDiscountDeselected: (d: SaleDiscount) => Promise<void>
}

const InvoiceChangeDiscountsGrid = (props: InvoiceChangeDiscountsGridProps) => {
    const gridQuery = useInvoiceDiscountsGridQuery(props);

    return (
        <AgTechGrid
            data={{
                itemQuery: gridQuery
            }}
            header={props.header}
            columns={[
                {
                    header: 'Name',
                    value: d => d.entity.name,
                    size: 3
                },
                {
                    header: 'Discount',
                    value: d => d.entity.isPercentage ?  `${d.entity.amount}%` : AgTechMoneyFormatter.format(d.entity.amount),
                    size: 1
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.entity.name === curr.entity.name &&
                    prev.entity.amount === curr.entity.amount;
            }}
            selectionConfig={{
                isItemSelected: discount => discount.isSelected,
                onItemSelected: async selectedDiscount => {
                    props.onDiscountSelected(selectedDiscount.entity);
                },
                onItemDeSelected: async deselectedDiscount => {
                    props.onDiscountDeselected(deselectedDiscount.entity);
                }
            }}
            actions={{
                rendering: discountSelection => {
                    return (
                        <AgTechClickableIcon
                            icon='bi bi-pencil-square'
                            tooltip="Update discount"
                            onClick={e => SaleDiscountDialog.Open(e, {
                                discount: discountSelection.entity
                            })}
                        />
                    )
                }
            }}
        />
    )
}

const useInvoiceDiscountsGridQuery = (props: InvoiceChangeDiscountsGridProps) => {
    const saleDiscountsQuery = useAgTechQuery({
        query: DiscountDataGetAllDiscountsQuery
    });

    return useAgTechQueryResultProjection({
        queryRun: saleDiscountsQuery,
        projection: discounts => {
            return {
                items: mergeGridEntitySelections<SaleDiscount>({
                    entitySelections: props.discountSelections,
                    entities: Object.values(discounts)
                }),
                context: {
                    allDiscounts: discounts
                }
            }
        }
    });
}

export default InvoiceChangeDiscountsGrid;